import type { Session } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { supabase } from "./supabase";

interface AuthState {
  session: Session | null;
  /** 初回のセッション復元が終わるまで true */
  loading: boolean;
}

const AuthContext = createContext<AuthState>({ session: null, loading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ session: null, loading: true });

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (active) setState({ session: data.session, loading: false });
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) setState({ session, loading: false });
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return <AuthContext value={state}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * ブラウザがパスキーに対応しているか。
 *
 * WebAuthn は保護されたコンテキスト(HTTPS または localhost)でしか使えないため、
 * `window.PublicKeyCredential` の有無で判定できる。
 */
export function isPasskeySupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window;
}

/** ユーザー操作のキャンセルは失敗として扱わないための判定 */
export function isUserCancellation(error: unknown): boolean {
  return (
    error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError")
  );
}

export function errorMessageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  // Supabase(PostgREST)の失敗は Error ではなくただのオブジェクトで返る。
  // そのまま文字列にすると "[object Object]" になり、何が起きたか分からなくなる
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const message = typeof record.message === "string" ? record.message : null;
    const details = typeof record.details === "string" ? record.details : null;
    if (message !== null) return details === null ? message : `${message} (${details})`;
  }
  return String(error);
}

/** パスキーとみなす認証方式。Supabase の表記ゆれに備えて複数を見る */
const PASSKEY_METHODS = new Set(["webauthn", "passkey"]);

/** アクセストークンの中身。**検証はしない**(表示の出し分けにだけ使う) */
function payloadOf(accessToken: string): Record<string, unknown> | null {
  const encoded = accessToken.split(".")[1];
  if (encoded === undefined) return null;
  try {
    const binary = atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * パスキーで入ったかどうか。
 *
 * マジックリンクで入った人に「この端末にパスキーを登録しませんか」と促すために使う。
 * **分からないときは true を返す。** 判定できないからといって毎回促すのは煩わしい。
 */
export function signedInWithPasskey(accessToken: string): boolean {
  const methods = payloadOf(accessToken)?.amr;
  if (!Array.isArray(methods)) return true;
  return methods.some((entry) => {
    if (typeof entry !== "object" || entry === null) return false;
    const method = (entry as Record<string, unknown>).method;
    return typeof method === "string" && PASSKEY_METHODS.has(method);
  });
}
